import { z } from 'zod';
// ============================================================
// Enums
// ============================================================
export const FamilyDisplayModeSchema = z.enum([
    'full_names',
    'single_parent_father',
    'single_parent_mother',
    'self_only',
]);
export const TemplateCategorySchema = z.enum([
    'CLASSIC',
    'MODERN',
    'FLORAL',
    'MINIMAL',
    'ELEGANT',
    'NATURAL',
]);
export const InvitationStatusSchema = z.enum([
    'DRAFT',
    'PUBLISHED',
    'EXPIRED',
    'DELETED',
]);
// ============================================================
// Section Ordering
// ============================================================
export const REORDERABLE_SECTIONS = [
    'greeting',
    'parents',
    'ceremony',
    'gallery',
    'contacts',
    'accounts',
    'rsvp',
    'guestbook',
    'timeline',
    'schedule',
    'notices',
];
export const DEFAULT_SECTION_ORDER = [...REORDERABLE_SECTIONS];
export const SECTION_LABELS = {
    greeting: '인사말',
    parents: '신랑/신부 정보',
    ceremony: '예식 정보',
    gallery: '갤러리',
    contacts: '연락처',
    accounts: '계좌번호',
    rsvp: '참석 여부',
    guestbook: '방명록',
    timeline: '타임라인',
    schedule: '식순',
    notices: '안내사항',
};
export function sanitizeSectionOrder(order) {
    if (!order)
        return [...DEFAULT_SECTION_ORDER];
    const seen = new Set();
    const valid = order.filter((id) => {
        if (seen.has(id) || !REORDERABLE_SECTIONS.includes(id))
            return false;
        seen.add(id);
        return true;
    });
    const missing = DEFAULT_SECTION_ORDER.filter((id) => !valid.includes(id));
    return [...valid, ...missing];
}
// ============================================================
// 타임라인 / 일정 / 안내사항
// ============================================================
export const TimelineItemSchema = z.object({
    date: z.string().optional(),
    title: z.string(),
    description: z.string().optional(),
});
export const TimelineSchema = z.array(TimelineItemSchema).default([]);
export const ScheduleItemSchema = z.object({
    time: z.string(),
    title: z.string(),
    description: z.string().optional(),
});
export const ScheduleSchema = z.array(ScheduleItemSchema).default([]);
export const NoticeItemSchema = z.object({
    icon: z.string().optional(),
    title: z.string(),
    description: z.string().optional(),
});
export const NoticesSchema = z.array(NoticeItemSchema).default([]);
// ============================================================
// Sub-schemas (strict — 발행 시 검증용)
// ============================================================
export const PersonSchema = z.object({
    name: z.string().min(1, '이름을 입력해주세요'),
    fatherName: z.string().optional(),
    motherName: z.string().optional(),
    isDeceased: z
        .object({
        father: z.boolean().optional(),
        mother: z.boolean().optional(),
    })
        .optional(),
    phone: z.string().optional(),
    relation: z.string().optional(),
    displayMode: FamilyDisplayModeSchema.optional(),
});
export const AccountSchema = z.object({
    bank: z.string().min(1, '은행을 선택해주세요'),
    accountNumber: z.string().min(1, '계좌번호를 입력해주세요'),
    accountHolder: z.string().min(1, '예금주를 입력해주세요'),
});
export const ParentAccountsSchema = z.object({
    father: z.array(AccountSchema).default([]),
    mother: z.array(AccountSchema).default([]),
}).optional();
export const VenueSchema = z.object({
    name: z.string().min(1, '예식장 이름을 입력해주세요'),
    hall: z.string().optional(),
    address: z.string().min(1, '주소를 입력해주세요'),
    lat: z.number().optional(),
    lng: z.number().optional(),
    tel: z.string().optional(),
    transportation: z.string().optional(),
    parking: z.string().optional(),
});
export const GreetingFontSizeSchema = z.enum(['sm', 'md', 'lg', 'xl', 'custom']).default('md');
export const ContentSchema = z.object({
    greeting: z.string().optional(),
    notice: z.string().optional(),
    greetingFontSize: GreetingFontSizeSchema.optional(),
    greetingFontSizeCustom: z.number().optional(),
});
export const GallerySchema = z.object({
    coverImage: z.string().url().optional(),
    images: z.array(z.string().url()).default([]),
});
export const RsvpFieldsSchema = z.object({
    phone: z.boolean().default(true),
    guestCount: z.boolean().default(true),
    meal: z.boolean().default(true),
    message: z.boolean().default(true),
});
export const SettingsSchema = z.object({
    showParents: z.boolean().default(true),
    showAccounts: z.boolean().default(true),
    showMap: z.boolean().default(true),
    enableRsvp: z.boolean().default(true),
    rsvpFields: RsvpFieldsSchema.optional(),
    backgroundColor: z.string().optional(),
    textColor: z.string().optional(),
    fontFamily: z.enum(['noto-sans', 'pretendard', 'noto-serif', 'gowun-batang', 'nanum-myeongjo', 'kopub-batang']).optional(),
    textScale: z.enum(['sm', 'md', 'lg']).default('md'),
    sectionOrder: z.array(z.string()).optional(),
    calendarStyle: z.enum(['none', 'calendar', 'countdown', 'minimal']).default('calendar'),
    galleryLayout: z.enum(['grid-2', 'grid-3', 'grid-2-1', 'single-column', 'masonry', 'carousel', 'filmstrip', 'highlight']).optional(),
    galleryAspectRatio: z.enum(['1:1', '4:3', '3:4', '16:9']).optional(),
    requirePassword: z.boolean().default(true),
    password: z.string().optional(),
    isPublic: z.boolean().default(true),
    customSlug: z.string().optional(),
    expiresAt: z.string().optional(),
    sectionSpacing: z.record(z.string(), z.string()).optional(),
    fadeSpeed: z.enum(['slow', 'normal', 'fast', 'none']).optional(),
});
export const EnabledSectionsSchema = z.object({
    greeting: z.boolean().optional(),
    gallery: z.boolean().optional(),
    account: z.boolean().optional(),
    rsvp: z.boolean().optional(),
    guestbook: z.boolean().optional(),
    ending: z.boolean().optional(),
    bgm: z.boolean().optional(),
    timeline: z.boolean().optional(),
    schedule: z.boolean().optional(),
    notices: z.boolean().optional(),
    contacts: z.boolean().optional(),
});
export const EndingSchema = z.object({
    imageUrl: z.string().url().optional(),
    message: z.string().optional(),
});
export const ShareSchema = z.object({
    ogImage: z.string().url().optional(),
    ogTitle: z.string().optional(),
    ogDescription: z.string().optional(),
});
export const BgmSchema = z.object({
    url: z.string(),
    title: z.string().optional(),
    autoplay: z.boolean().default(true),
    loop: z.boolean().default(true),
    volume: z.number().min(0).max(1).default(0.5),
});
// ============================================================
// InvitationData — JSONB `data` 컬럼 스키마
// ============================================================
/** data JSONB 내부 Person (이름 필수 아님 — 드래프트 허용) */
const DataPersonSchema = z.object({
    name: z.string().default(''),
    fatherName: z.string().optional(),
    motherName: z.string().optional(),
    isDeceased: z.object({
        father: z.boolean().optional(),
        mother: z.boolean().optional(),
    }).optional(),
    phone: z.string().optional(),
    fatherPhone: z.string().optional(),
    motherPhone: z.string().optional(),
    relation: z.string().optional(),
    displayMode: FamilyDisplayModeSchema.optional(),
    account: AccountSchema.optional(),
    parentAccounts: ParentAccountsSchema,
});
/** data JSONB 내부 Venue (주소 필수 아님 — 드래프트 허용) */
const DataVenueSchema = z.object({
    name: z.string().default(''),
    hall: z.string().optional(),
    address: z.string().default(''),
    lat: z.number().optional(),
    lng: z.number().optional(),
    tel: z.string().optional(),
    transportation: z.string().optional(),
    parking: z.string().optional(),
});
const DEFAULT_PERSON = { name: '' };
const DEFAULT_VENUE = { name: '', address: '' };
export const InvitationDataSchema = z.object({
    groom: DataPersonSchema.default(DEFAULT_PERSON),
    bride: DataPersonSchema.default(DEFAULT_PERSON),
    wedding: z.object({
        date: z.string().optional(),
        venue: DataVenueSchema.default(DEFAULT_VENUE),
    }).default(() => ({ venue: DEFAULT_VENUE })),
    content: ContentSchema.default({}),
    gallery: GallerySchema.default(() => ({ images: [] })),
    aiPhotoUrl: z.string().optional(),
    settings: SettingsSchema.partial().default({}),
    enabledSections: EnabledSectionsSchema.optional(),
    ending: EndingSchema.optional(),
    share: ShareSchema.optional(),
    bgm: BgmSchema.optional(),
    timeline: TimelineSchema,
    schedule: ScheduleSchema,
    notices: NoticesSchema,
    customTheme: z.any().optional(),
}).default(() => ({
    groom: DEFAULT_PERSON,
    bride: DEFAULT_PERSON,
    wedding: { venue: DEFAULT_VENUE },
    content: {},
    gallery: { images: [] },
    settings: {},
    timeline: [],
    schedule: [],
    notices: [],
}));
// ============================================================
// 하위 호환: ExtendedDataSchema (deprecated — data로 통합)
// ============================================================
const ExtendedPersonSchema = z.object({
    fatherName: z.string().optional(),
    motherName: z.string().optional(),
    isDeceased: z.object({
        father: z.boolean().optional(),
        mother: z.boolean().optional(),
    }).optional(),
    phone: z.string().optional(),
    relation: z.string().optional(),
    displayMode: FamilyDisplayModeSchema.optional(),
    account: AccountSchema.optional(),
    parentAccounts: ParentAccountsSchema,
}).partial();
export const ExtendedDataSchema = z.object({
    templateId: z.string().optional(),
    groom: ExtendedPersonSchema.optional(),
    bride: ExtendedPersonSchema.optional(),
    venue: z.object({
        hall: z.string().optional(),
        lat: z.number().optional(),
        lng: z.number().optional(),
        tel: z.string().optional(),
        transportation: z.string().optional(),
        parking: z.string().optional(),
    }).optional(),
    content: z.object({
        notice: z.string().optional(),
    }).optional(),
    gallery: z.object({
        coverImage: z.string().url().optional(),
    }).optional(),
    settings: SettingsSchema.partial().optional(),
    ending: EndingSchema.optional(),
    share: ShareSchema.optional(),
    bgm: BgmSchema.optional(),
    enabledSections: EnabledSectionsSchema.optional(),
    customTheme: z.any().optional(),
}).default({});
// ============================================================
// Main Invitation Schema
// ============================================================
export const InvitationSchema = z.object({
    id: z.string(),
    userId: z.string(),
    templateId: z.string(),
    data: InvitationDataSchema,
    slug: z.string().nullable().optional(),
    isPasswordProtected: z.boolean().default(false),
    status: InvitationStatusSchema.default('DRAFT'),
    viewCount: z.number().default(0),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    expiresAt: z.string().datetime().optional(),
});
// ============================================================
// Create/Update
// ============================================================
/** 슬러그 검증: 3~50자, 소문자 영숫자 + 하이픈, 시작/끝은 영숫자 */
export const SlugSchema = z.string()
    .regex(/^[a-z0-9][a-z0-9-]{1,48}[a-z0-9]$/, '슬러그는 3~50자의 소문자 영숫자와 하이픈만 허용됩니다')
    .nullable()
    .optional();
export const CreateInvitationSchema = z.object({
    templateId: z.string().min(1, '템플릿을 선택해주세요'),
    slug: SlugSchema,
    data: z.object({
        groom: z.object({ name: z.string().default('') }).passthrough().optional(),
        bride: z.object({ name: z.string().default('') }).passthrough().optional(),
        wedding: z.object({
            date: z.string().datetime('올바른 날짜 형식을 입력해주세요').optional(),
            venue: z.object({
                name: z.string().default(''),
                address: z.string().optional(),
            }).passthrough().optional(),
        }).optional(),
        content: z.object({
            greeting: z.string().optional(),
        }).optional(),
    }).passthrough().optional(),
});
export const UpdateInvitationSchema = z.object({
    templateId: z.string().optional(),
    data: InvitationDataSchema.optional(),
    slug: SlugSchema,
    status: InvitationStatusSchema.optional(),
    isPasswordProtected: z.boolean().optional(),
    expiresAt: z.string().datetime().optional(),
});
//# sourceMappingURL=invitation.js.map